import React, {useCallback, useRef} from 'react';

export interface DragPreview {
    index: number;
    position: number;
}

export interface DragLimits {
    min: number;
    max: number;
}

interface UseInnerDrawerDragProps {
    // pixels per mm of the drawing
    ratio: number;
    positionOf: (index: number) => number;
    // how far the box can go and stay inside the cabinet
    limitsOf: (index: number) => DragLimits;
    onStart: (index: number) => void;
    // where the box is mid-drag, for everything that shows it; null when done
    onPreview: (preview: DragPreview) => void;
    // the same commit typing a position takes; a drag is no stricter than that
    commit: (index: number, position: number) => void;
}

// the box's own input and buttons keep their keys and clicks
const fromControl = (event: React.SyntheticEvent<HTMLElement>) =>
    !!(event.target as HTMLElement).closest('input, button');

// a box already outside, from saved data, is not pulled in, only kept from
// going further out
export const keepWithin = (
    position: number,
    start: number,
    {min, max}: DragLimits
) => {
    const lowest = Math.min(min, start);
    const highest = Math.max(max, start);

    return Math.max(lowest, Math.min(highest, position));
};

// Moving a whole drawer box: nothing reaches the form until the drop. It moves
// in whole millimetres from where it started, so a drag back to the start
// changes nothing, where rounding the result would snap 378.75 to 379.
export const useInnerDrawerDrag = ({
    ratio,
    positionOf,
    limitsOf,
    onStart,
    onPreview,
    commit,
}: UseInnerDrawerDragProps) => {
    // the listeners below outlive the render they were created in
    const previewRef = useRef<DragPreview>(null);

    const showPreview = useCallback(
        (next: DragPreview) => {
            previewRef.current = next;
            onPreview(next);
        },
        [onPreview]
    );

    // pointer events rather than mouse, so a finger drags the same as a cursor
    const startDrag = useCallback(
        (event: React.PointerEvent<HTMLElement>) => {
            if (event.button !== 0 || fromControl(event)) return;

            event.preventDefault();

            const index = Number(event.currentTarget.dataset.index);
            const start = positionOf(index);
            const limits = limitsOf(index);
            const startY = event.clientY;

            onStart(index);

            const move = (moveEvent: PointerEvent) => {
                showPreview({
                    index,
                    // up the drawing is up the cabinet
                    position: keepWithin(
                        start +
                            Math.round((startY - moveEvent.clientY) / ratio),
                        start,
                        limits
                    ),
                });
            };

            const drop = () => {
                document.removeEventListener('pointermove', move);
                document.removeEventListener('pointerup', drop);
                document.removeEventListener('pointercancel', drop);

                const last = previewRef.current;

                showPreview(null);

                // a drag back to where it started commits nothing, as typing
                // the same number does
                if (last) commit(index, last.position);
            };

            document.addEventListener('pointermove', move);
            document.addEventListener('pointerup', drop);
            // a touch gesture the browser takes over ends as a cancel, not an up
            document.addEventListener('pointercancel', drop);
        },
        [commit, limitsOf, onStart, positionOf, ratio, showPreview]
    );

    // a millimetre a press, ten with shift; in the box's input the arrows
    // still move the caret
    const nudge = useCallback(
        (event: React.KeyboardEvent<HTMLElement>) => {
            if (fromControl(event)) return;

            const step =
                event.key === 'ArrowUp'
                    ? 1
                    : event.key === 'ArrowDown'
                      ? -1
                      : 0;

            if (step === 0) return;

            event.preventDefault();

            const index = Number(event.currentTarget.dataset.index);
            const start = positionOf(index);

            commit(
                index,
                keepWithin(
                    start + step * (event.shiftKey ? 10 : 1),
                    start,
                    limitsOf(index)
                )
            );
        },
        [commit, limitsOf, positionOf]
    );

    return {startDrag, nudge};
};
